// Digit conversion utility for English and Nepali digits
// Supports both individual digit conversion and mathematical operations

// Digit mappings
const englishToNepaliDigits = {
    '0': '०', '1': '१', '2': '२', '3': '३', '4': '४',
    '5': '५', '6': '६', '7': '७', '8': '८', '9': '९'
};

const nepaliToEnglishDigits = {
    '०': '0', '१': '1', '२': '2', '३': '3', '४': '4',
    '५': '5', '६': '6', '७': '7', '८': '8', '९': '9'
};

/**
 * Convert English digits to Nepali digits
 * @param {string|number} input - Input string or number
 * @returns {string} - String with Nepali digits
 */
export const convertToNepaliDigits = (input) => {
    if (input === null || input === undefined || input === '') return '';
    
    const inputStr = String(input);
    return inputStr.split('').map(char => englishToNepaliDigits[char] || char).join('');
};

/**
 * Convert Nepali digits to English digits
 * @param {string} input - Input string with Nepali digits
 * @returns {string} - String with English digits
 */
export const convertToEnglishDigits = (input) => {
    if (input === null || input === undefined || input === '') return '';
    
    const inputStr = String(input);
    return inputStr.split('').map(char => nepaliToEnglishDigits[char] || char).join('');
};

/**
 * Normalize input to English digits for calculations
 * @param {string} input - Input string (can contain mixed digits)
 * @returns {string} - String with only English digits
 */
export const normalizeToEnglish = (input) => {
    if (input === null || input === undefined || input === '') return '';
    
    const inputStr = String(input);
    return inputStr.split('').map(char => nepaliToEnglishDigits[char] || char).join('');
};

/**
 * Parse number from mixed digit input
 * @param {string} input - Input string (can contain mixed digits)
 * @returns {number} - Parsed number
 */
export const parseNumber = (input) => {
    if (input === null || input === undefined || input === '') return 0;
    
    const normalized = normalizeToEnglish(input);
    const parsed = parseFloat(normalized);
    return isNaN(parsed) ? 0 : parsed;
};

/**
 * Format number with specified digit type
 * @param {number} number - Number to format
 * @param {string} digitType - 'english' or 'nepali'
 * @param {number} decimals - Number of decimal places
 * @returns {string} - Formatted number string
 */
export const formatNumber = (number, digitType = 'english', decimals = 2) => {
    if (number === null || number === undefined || isNaN(number)) return '';
    
    const formatted = Number(number).toFixed(decimals);
    return digitType === 'nepali' ? convertToNepaliDigits(formatted) : formatted;
};

/**
 * Perform mathematical operations with mixed digit support
 * @param {string} expression - Mathematical expression (e.g., "1+1", "१+१")
 * @param {string} digitType - 'english' or 'nepali' for result
 * @returns {string} - Result in specified digit type
 */
export const calculateWithMixedDigits = (expression, digitType = 'english') => {
    if (!expression) return '';
    
    try {
        // Normalize the expression to English digits
        const normalizedExpression = normalizeToEnglish(expression);
        
        // Replace mathematical operators with spaces for parsing
        const operators = ['+', '-', '*', '/', '×', '÷'];
        let parsedExpression = normalizedExpression;
        
        operators.forEach(op => {
            const nepaliOp = op === '*' ? '×' : op === '/' ? '÷' : op;
            parsedExpression = parsedExpression.replace(new RegExp(`\\${nepaliOp}`, 'g'), op);
        });
        
        // Extract numbers and operators
        const numbers = parsedExpression.split(/[+\-*/\\s]+/).filter(n => n.trim());
        const foundOperators = parsedExpression.match(/[+\-*/]/g) || [];
        
        if (numbers.length < 2 || foundOperators.length === 0) {
            return expression; // Return original if no valid operation found
        }
        
        let result = parseFloat(numbers[0]);
        
        for (let i = 0; i < foundOperators.length && i + 1 < numbers.length; i++) {
            const operator = foundOperators[i];
            const nextNumber = parseFloat(numbers[i + 1]);
            
            switch (operator) {
                case '+':
                    result += nextNumber;
                    break;
                case '-':
                    result -= nextNumber;
                    break;
                case '*':
                    result *= nextNumber;
                    break;
                case '/':
                    if (nextNumber !== 0) {
                        result /= nextNumber;
                    } else {
                        throw new Error('Division by zero');
                    }
                    break;
                default:
                    break;
            }
        }
        
        // Format result based on digit type
        return formatNumber(result, digitType);
        
    } catch (error) {
        console.error('Calculation error:', error);
        return expression; // Return original input if calculation fails
    }
};

/**
 * Auto-calculate total value (quantity * rate)
 * @param {string} quantity - Quantity input (can be mixed digits)
 * @param {string} rate - Rate input (can be mixed digits)
 * @param {string} digitType - 'english' or 'nepali' for result
 * @returns {string} - Calculated total
 */
export const calculateTotal = (quantity, rate, digitType = 'english') => {
    console.log('calculateTotal called with:', { quantity, rate, digitType });
    const qty = parseNumber(quantity);
    const rte = parseNumber(rate);
    console.log('Parsed values:', { qty, rte });
    const total = qty * rte;
    console.log('Raw total:', total);
    const result = formatNumber(total, digitType);
    console.log('Formatted result:', result);
    return result;
};

/**
 * Check if input contains Nepali digits
 * @param {string} input - Input string
 * @returns {boolean} - True if contains Nepali digits
 */
export const containsNepaliDigits = (input) => {
    if (!input) return false;
    return Object.keys(nepaliToEnglishDigits).some(digit => input.includes(digit));
};

/**
 * Check if input contains English digits
 * @param {string} input - Input string
 * @returns {boolean} - True if contains English digits
 */
export const containsEnglishDigits = (input) => {
    if (!input) return false;
    return /[0-9]/.test(input);
};

/**
 * Get digit type preference from input
 * @param {string} input - Input string
 * @returns {string} - 'nepali', 'english', or 'mixed'
 */
export const getDigitType = (input) => {
    if (!input) return 'english';
    
    const hasNepali = containsNepaliDigits(input);
    const hasEnglish = containsEnglishDigits(input);
    
    if (hasNepali && hasEnglish) return 'mixed';
    if (hasNepali) return 'nepali';
    if (hasEnglish) return 'english';
    return 'english';
};

/**
 * Smart input handler for form fields
 * @param {string} value - Input value
 * @param {string} fieldType - 'number', 'text', 'calculation'
 * @returns {object} - { normalizedValue, displayValue, digitType }
 */
export const handleSmartInput = (value, fieldType = 'text') => {
    if (!value) return { normalizedValue: '', displayValue: '', digitType: 'english' };
    
    const digitType = getDigitType(value);
    
    if (fieldType === 'number') {
        const parsed = parseNumber(value);
        return {
            normalizedValue: parsed.toString(),
            displayValue: value, // Keep original input for display
            digitType
        };
    } else if (fieldType === 'calculation') {
        const result = calculateWithMixedDigits(value, digitType);
        return {
            normalizedValue: normalizeToEnglish(result),
            displayValue: result,
            digitType
        };
    } else {
        return {
            normalizedValue: normalizeToEnglish(value),
            displayValue: value,
            digitType
        };
    }
};

const digitConverter = {
    convertToNepaliDigits,
    convertToEnglishDigits,
    normalizeToEnglish,
    parseNumber,
    formatNumber,
    calculateWithMixedDigits,
    calculateTotal,
    containsNepaliDigits,
    containsEnglishDigits,
    getDigitType,
    handleSmartInput
};

export default digitConverter;
